import { useState, useEffect, useCallback } from "react";
import { BarChart3, TrendingUp, Users, CreditCard, Eye, Radio, FileDown, Loader2, RefreshCw } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { api, euro, apiError, API } from "@/lib/api";
import { toast } from "sonner";

const RED = "#ff0033";
const PERIODS = [["7", "7 Tage"], ["30", "30 Tage"], ["90", "90 Tage"]];
const dLabel = (d) => { const [y, m, day] = d.split("-"); return `${day}.${m}.`; };

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="border border-white/10 bg-[#0A0A0A] p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest"><Icon size={14} className="text-[#ff0033]" /> {label}</div>
    <div className="font-heading text-2xl text-white mt-2">{value}</div>
    {sub && <div className="text-neutral-500 text-xs mt-1">{sub}</div>}
  </div>
);

const Panel = ({ title, children, right }) => (
  <div className="border border-white/10 bg-[#0A0A0A] p-5">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading text-lg uppercase tracking-tight text-white">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const tooltipStyle = { background: "#111", border: "1px solid #333", borderRadius: 0, fontSize: 12 };

const BarList = ({ items, labelKey, valueKey, fmt = (v) => v }) => {
  const max = Math.max(1, ...items.map((i) => i[valueKey]));
  if (!items.length) return <p className="text-neutral-600 text-sm">Keine Daten im Zeitraum.</p>;
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} data-testid="barlist-row">
          <div className="flex justify-between text-sm text-neutral-300 mb-1"><span className="truncate pr-3">{it[labelKey] || "—"}</span><span className="text-white shrink-0">{fmt(it[valueKey])}</span></div>
          <div className="h-1.5 bg-white/5"><div className="h-full bg-[#ff0033]" style={{ width: `${(it[valueKey] / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

const Analytics = () => {
  const [days, setDays] = useState("30");
  const [biz, setBiz] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([
        api.get(`/admin/analytics/business?days=${days}`),
        api.get(`/admin/analytics/traffic?days=${days}`),
      ]);
      setBiz(b.data); setTraffic(t.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const token = localStorage.getItem("gca_token");
    fetch(`${API}/admin/analytics/business.csv?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((blob) => {
        const url = URL.createObjectURL(blob); const a = document.createElement("a");
        a.href = url; a.download = `analytics-${days}tage.csv`; a.click(); URL.revokeObjectURL(url);
      }).catch(() => toast.error("Export fehlgeschlagen"));
  };

  const k = biz?.kpis; const tk = traffic?.kpis;
  const fn = traffic?.funnel;

  return (
    <div data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <BarChart3 size={22} className="text-[#ff0033]" />
          <h2 className="font-heading text-2xl uppercase tracking-tight text-white">Analytics</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-white/10">
            {PERIODS.map(([v, l]) => (
              <button key={v} onClick={() => setDays(v)} data-testid={`analytics-period-${v}`}
                className={`px-3 py-2 font-mono-lab text-[11px] uppercase tracking-widest ${days === v ? "bg-[#ff0033] text-white" : "text-neutral-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <button onClick={load} data-testid="analytics-refresh" className="border border-white/15 text-neutral-300 px-3 py-2 hover:border-[#ff0033] hover:text-white transition-colors"><RefreshCw size={15} /></button>
          <button onClick={exportCsv} data-testid="analytics-export" className="flex items-center gap-2 border border-white/15 text-neutral-300 font-mono-lab text-[11px] uppercase tracking-widest px-3 py-2 hover:border-[#ff0033] hover:text-white transition-colors"><FileDown size={14} /> CSV</button>
        </div>
      </div>

      {loading && !biz ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#ff0033]" /></div>
      ) : (
        <div className="space-y-8">
          {/* ===== Geschäft ===== */}
          <section>
            <h3 className="font-mono-lab text-xs tracking-widest text-neutral-500 uppercase mb-3">Umsatz & Buchungen</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Kpi icon={TrendingUp} label="Gesamtumsatz" value={euro(k?.grand_total || 0)} sub={`Kurse ${euro(k?.revenue_total || 0)} · TrackMan ${euro(k?.trackman_revenue || 0)} · Gutschein ${euro(k?.voucher_revenue || 0)}`} testid="kpi-revenue" />
              <Kpi icon={CreditCard} label="Bezahlte Buchungen" value={k?.paid_count || 0} sub={`Ø Bestellwert ${euro(k?.avg_order_value || 0)}`} testid="kpi-paid" />
              <Kpi icon={Users} label="Buchungen gesamt" value={k?.bookings_total || 0} sub={`Offene Zahlungen: ${k?.open_payments || 0}`} testid="kpi-bookings" />
              <Kpi icon={CreditCard} label="Conversion" value={`${k?.conversion_rate || 0}%`} sub={`${k?.requested_paid || 0} von ${k?.requested || 0} Aufforderungen bezahlt`} testid="kpi-conversion" />
            </div>

            <Panel title="Umsatzverlauf">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={biz?.revenue_series || []} margin={{ left: -10, right: 10, top: 5 }}>
                  <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={RED} stopOpacity={0.5} /><stop offset="100%" stopColor={RED} stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#1f1f1f" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={dLabel} tick={{ fill: "#666", fontSize: 11 }} axisLine={{ stroke: "#333" }} tickLine={false} minTickGap={24} />
                  <YAxis tick={{ fill: "#666", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={dLabel} formatter={(v) => [euro(v), "Umsatz"]} />
                  <Area type="monotone" dataKey="revenue" stroke={RED} strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            <div className="grid lg:grid-cols-3 gap-4 mt-4">
              <Panel title="Top-Kurse (Umsatz)"><BarList items={biz?.top_courses || []} labelKey="name" valueKey="revenue" fmt={euro} /></Panel>
              <Panel title="Umsatz je Kategorie"><BarList items={biz?.by_category || []} labelKey="category" valueKey="revenue" fmt={euro} /></Panel>
              <Panel title="Buchungen je Quelle"><BarList items={biz?.by_source || []} labelKey="source" valueKey="count" /></Panel>
            </div>
          </section>

          {/* ===== Website-Traffic ===== */}
          <section>
            <h3 className="font-mono-lab text-xs tracking-widest text-neutral-500 uppercase mb-3">Website-Traffic (cookie-frei)</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Kpi icon={Eye} label="Seitenaufrufe" value={tk?.pageviews || 0} testid="kpi-pageviews" />
              <Kpi icon={Users} label="Besucher" value={tk?.visitors || 0} sub={`${tk?.views_per_visitor || 0} Seiten/Besucher`} testid="kpi-visitors" />
              <Kpi icon={Radio} label="Jetzt aktiv (30 Min.)" value={tk?.realtime_visitors || 0} sub={`${tk?.realtime_views || 0} Aufrufe`} testid="kpi-realtime" />
              <Kpi icon={TrendingUp} label="Buchungs-Funnel" value={`${fn?.booking_complete || 0} / ${fn?.course_view || 0}`} sub="abgeschlossen / Kurs angesehen" testid="kpi-funnel" />
            </div>

            <Panel title="Seitenaufrufe im Zeitverlauf">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={traffic?.views_series || []} margin={{ left: -10, right: 10, top: 5 }}>
                  <defs><linearGradient id="vw" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4da3ff" stopOpacity={0.5} /><stop offset="100%" stopColor="#4da3ff" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#1f1f1f" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={dLabel} tick={{ fill: "#666", fontSize: 11 }} axisLine={{ stroke: "#333" }} tickLine={false} minTickGap={24} />
                  <YAxis tick={{ fill: "#666", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={dLabel} formatter={(v) => [v, "Aufrufe"]} />
                  <Area type="monotone" dataKey="views" stroke="#4da3ff" strokeWidth={2} fill="url(#vw)" />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            <div className="grid lg:grid-cols-2 gap-4 mt-4">
              <Panel title="Conversion-Funnel">
                <FunnelView fn={fn} />
              </Panel>
              <Panel title="Geräte">
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={traffic?.devices || []} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" hide /><YAxis type="category" dataKey="device" tick={{ fill: "#aaa", fontSize: 12 }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#ffffff08" }} formatter={(v) => [v, "Aufrufe"]} />
                    <Bar dataKey="views" radius={[0, 0, 0, 0]}>{(traffic?.devices || []).map((_, i) => <Cell key={i} fill={RED} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </div>

            <div className="grid lg:grid-cols-2 gap-4 mt-4">
              <Panel title="Meistbesuchte Seiten"><BarList items={traffic?.top_pages || []} labelKey="path" valueKey="views" /></Panel>
              <Panel title="Traffic-Quellen"><BarList items={traffic?.sources || []} labelKey="source" valueKey="views" /></Panel>
            </div>

            <div className="mt-4">
              <Panel title="Newsletter-Auswertung"><NewsletterStats /></Panel>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const NewsletterStats = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/newsletters").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return <p className="text-neutral-500 text-sm">Lädt…</p>;
  if (items.length === 0) return <p className="text-neutral-500 text-sm" data-testid="nl-stats-empty">Noch keine Newsletter versendet.</p>;
  return (
    <div className="overflow-x-auto" data-testid="nl-stats">
      <table className="w-full text-sm min-w-[560px]">
        <thead><tr className="text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest text-left border-b border-white/10">
          <th className="p-2">Datum</th><th className="p-2">Betreff</th><th className="p-2">Empfänger</th><th className="p-2">Öffnungsrate</th><th className="p-2">Klickrate</th><th className="p-2">Klicks je Kurs</th>
        </tr></thead>
        <tbody>
          {items.map((n) => (
            <tr key={n.id} className="border-b border-white/5 text-neutral-300">
              <td className="p-2 whitespace-nowrap">{n.created_at ? new Date(n.created_at).toLocaleDateString("de-DE") : "—"}</td>
              <td className="p-2 max-w-[220px] truncate text-white">{n.subject}</td>
              <td className="p-2">{n.recipients}</td>
              <td className="p-2">{n.opens} <span className="text-neutral-500">({n.open_rate} %)</span></td>
              <td className="p-2">{n.clicks} <span className="text-neutral-500">({n.click_rate} %)</span></td>
              <td className="p-2 text-xs text-neutral-400">{Object.entries(n.click_details || {}).map(([k, v]) => `${k.replace("book_", "")}: ${v}`).join(" · ") || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const FunnelView = ({ fn }) => {
  const steps = [
    ["Kurs angesehen", fn?.course_view || 0],
    ["Buchung gestartet", fn?.booking_start || 0],
    ["Buchung abgeschlossen", fn?.booking_complete || 0],
  ];
  const max = Math.max(1, steps[0][1]);
  return (
    <div className="space-y-3">
      {steps.map(([label, val], i) => {
        const prev = i === 0 ? val : steps[i - 1][1];
        const rate = prev ? Math.round((val / prev) * 100) : 0;
        return (
          <div key={i}>
            <div className="flex justify-between text-sm text-neutral-300 mb-1">
              <span>{label}</span>
              <span className="text-white">{val}{i > 0 && <span className="text-neutral-500 text-xs ml-2">{rate}%</span>}</span>
            </div>
            <div className="h-6 bg-white/5"><div className="h-full bg-[#ff0033]/70 flex items-center px-2" style={{ width: `${(val / max) * 100}%`, minWidth: val ? "8%" : 0 }} /></div>
          </div>
        );
      })}
    </div>
  );
};

export default Analytics;
